import React, { useEffect, useState, useRef, useMemo } from "react";
import {
  Users,
  Clock,
  TrendingUp,
  Settings,
  Play,
  Square,
  Loader2,
  AlertCircle,
  ArrowUpRight,
  ArrowDownRight,
  ScrollTextIcon,
  CornerUpLeftIcon,
} from "lucide-react";
import { TextField, MenuItem } from "@mui/material";
import SockJS from "sockjs-client";
import { Client } from "@stomp/stompjs";
import {
  useGetEndpointsForTestSuite,
  useStartPerformanceTest,
  useStopPerformanceTest,
  useStopPerformanceSuite,
} from "../../../useQuery";
import { useNotification } from "../../../hooks";
import { API_BACKEND_URL } from "../../../Config/Config";

import PerformanceMetricsGraph from "./PerformanceMetricsGraph";

const INITIAL_METRICS = {
  currentVirtualUsers: 0,
  totalRequests: 0,
  currentRps: 0,
  avgResponseTime: 0,
  p90ResponseTime: 0,
  p95ResponseTime: 0,
  p99ResponseTime: 0,
  errorRate: 0,
};

const PARAMS_GRID_COLS = {
  FIXED: "xl:grid-cols-4",
  RAMP_UP: "xl:grid-cols-6",
  SPIKE: "xl:grid-cols-5",
};

const PerformanceTesting = ({ suite, onBack = () => {}, onViewPerformanceResults }) => {
  const notify = useNotification();
  const suiteId = suite?.id || suite?.testSuiteId;

  const { data: endpoints = [], isLoading: loadingEndpoints } =
    useGetEndpointsForTestSuite(suiteId);
  const { mutate: startTest } = useStartPerformanceTest();
  const { mutateAsync: stopTest, isPending: isStopping } = useStopPerformanceTest();
  const { mutateAsync: stopSuite } = useStopPerformanceSuite();

  const [activeRunId, setActiveRunId] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isRequestingStart, setIsRequestingStart] = useState(false);

  const [metrics, setMetrics] = useState(INITIAL_METRICS);
  const [history, setHistory] = useState([]);

  const [config, setConfig] = useState({
    virtualUsers: 10,
    duration: 1,
    rampUpTime: 30,
    thinkTime: 1,
    loadProfileType: "FIXED",
    startUsers: 1,
    endUsers: 50,
  });

  const isSpike = config.loadProfileType === "SPIKE";
  const isRampUp = config.loadProfileType === "RAMP_UP";
  const usesStartEndUsers = isSpike || isRampUp;

  const clientRef = useRef(null);
  const pendingStartRef = useRef(false);
  const notifyRef = useRef(notify);

  useEffect(() => {
    notifyRef.current = notify;
  }, [notify]);

  const hasTestCases = useMemo(
    () => endpoints.some((e) => e.testCaseId && e.testCaseId !== -1),
    [endpoints],
  );

  const wsEndpoint = useMemo(() => {
    const normalized = API_BACKEND_URL.replace(/\/api\/?$/, "");
    return `${normalized}/ws-performance`;
  }, []);

  useEffect(() => {
    const client = new Client({
      webSocketFactory: () => new SockJS(wsEndpoint),
      reconnectDelay: 5000,
      heartbeatIncoming: 10000,
      heartbeatOutgoing: 10000,
      onConnect: () => {
        setIsConnected(true);
        client.subscribe("/topic/active-tests", (message) => {
          try {
            const payload = JSON.parse(message.body);
            setMetrics((prev) => ({ ...prev, ...payload }));

            const now = new Date();
            const timeString = now.toLocaleTimeString("en-US", {
              hour12: false,
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
            });

            setHistory((prev) => {
              const newPoint = { ...payload, timestamp: timeString };
              const newHistory = [...prev, newPoint];

              if (newHistory.length > 60) {
                return newHistory.slice(newHistory.length - 60);
              }
              return newHistory;
            });

            if (payload.testRunId) {
              setActiveRunId((current) =>
                current === payload.testRunId ? current : payload.testRunId,
              );
              if (pendingStartRef.current) {
                pendingStartRef.current = false;
                setIsRequestingStart(false);
                if (notifyRef.current) notifyRef.current("Test started", "success");
              }
            }
          } catch (e) {
            /* tdo */
          }
        });
      },
      onDisconnect: () => setIsConnected(false),
      onStompError: () => setIsConnected(false),
    });

    clientRef.current = client;
    client.activate();

    return () => {
      client.deactivate();
      setIsConnected(false);
    };
  }, [wsEndpoint]);

  useEffect(() => {
    if (!activeRunId || !isConnected || !clientRef.current?.connected) return;
    let settleTimer;
    const completionSub = clientRef.current.subscribe(
      `/topic/test-completed/${activeRunId}`,
      () => {
        if (notifyRef.current) notifyRef.current("Test run completed", "success");
        pendingStartRef.current = false;
        setIsRequestingStart(false);
        settleTimer = setTimeout(() => {
          setActiveRunId((current) => (current === activeRunId ? null : current));
        }, 4000);
      },
    );
    return () => {
      clearTimeout(settleTimer);
      completionSub.unsubscribe();
    };
  }, [activeRunId, isConnected]);

  const handleStart = () => {
    if (!suiteId) return;
    setMetrics(INITIAL_METRICS);
    setHistory([]);
    setIsRequestingStart(true);
    pendingStartRef.current = true;

    const durationMinutes = Number(config.duration) || 1;

    const payload = {
      testSuitId: Number(suiteId),
      name: `${suite?.testSuiteName || "Suite"}`,
      virtualUsers: usesStartEndUsers ? Number(config.endUsers) : Number(config.virtualUsers),
      durationMinutes: durationMinutes,
      thinkTime: Math.round(Number(config.thinkTime) * 1000),
      metricsInterval: 1000,
      enableDetailedMetrics: true,
      loadProfile: {
        type: config.loadProfileType,
        startUsers: usesStartEndUsers ? Number(config.startUsers) : Number(config.virtualUsers),
        endUsers: usesStartEndUsers ? Number(config.endUsers) : Number(config.virtualUsers),
        rampUpTime: isRampUp ? Number(config.rampUpTime) : 0,
        holdTime: durationMinutes * 60,
        rampDownTime: isRampUp ? Number(config.rampUpTime) : 0,
      },
    };

    startTest(payload, {
      onError: (error) => {
        const msg = error?.response?.data?.error || error.message || "Failed to start";
        notify(msg, "error");
        setIsRequestingStart(false);
        pendingStartRef.current = false;
      },
    });
  };

  const handleStop = async () => {
    if (!activeRunId && !suiteId) return;
    try {
      notify("Stopping test...", "info");
      if (suiteId) {
        await stopSuite({ suiteId });
      } else {
        await stopTest({ testRunId: activeRunId });
      }
    } catch (error) {
      notify("Failed to stop test", "error");
    }
  };

  const summaryMetrics = [
    {
      label: "Total requests sent",
      value: metrics.totalRequests.toLocaleString(),
      unit: "",
      icon: null,
    },
    { label: "Requests/second", value: metrics.currentRps?.toFixed(2), unit: "", icon: null },
    {
      label: "Avg. response time",
      value: metrics.avgResponseTime?.toFixed(0),
      unit: "ms",
      color: "text-green-600",
    },
    {
      label: "P90 Response",
      value: metrics.p90ResponseTime?.toFixed(0) || "0",
      unit: "ms",
    },
    {
      label: "P99 Response",
      value: metrics.p99ResponseTime?.toFixed(0) || "0",
      unit: "ms",
    },
    {
      label: "Error rate",
      value: metrics.errorRate?.toFixed(2),
      unit: "%",
      color: metrics.errorRate > 0 ? "text-red-600" : "text-gray-900",
    },
  ];

  if (loadingEndpoints) {
    return (
      <div className="flex flex-col items-center justify-center h-full bg-white">
        <Loader2 className="h-8 w-8 text-blue-600 animate-spin mb-2" />
        <p className="text-gray-500 text-sm">Loading configuration...</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-white">
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-white sticky top-0 z-10">
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-500 cursor-pointer"
          >
            <CornerUpLeftIcon size={20} />
          </button>

          <div className="flex flex-col">
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-semibold text-gray-900">
                {suite?.testSuiteName || "Performance Test"}
              </h2>
              <span className="text-gray-400 text-sm">#{suiteId}</span>
              {activeRunId ? (
                <span className="px-2.5 py-0.5 rounded bg-amber-100 text-amber-700 text-xs font-bold border border-amber-200 animate-pulse">
                  RUNNING
                </span>
              ) : (
                <span className="px-2.5 py-0.5 rounded bg-green-100 text-green-700 text-xs font-bold border border-green-200">
                  READY
                </span>
              )}
              {!hasTestCases && <AlertCircle className="text-yellow-500" size={18} />}
            </div>
            <div className="flex items-center gap-1.5 mt-1">
              <div
                className={`w-2 h-2 rounded-full ${isConnected ? "bg-green-500" : "bg-red-500"}`}
              ></div>
              <span className="text-xs text-gray-500 font-medium">
                {isConnected ? "Socket Connected" : "Socket Disconnected"}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => onViewPerformanceResults && onViewPerformanceResults(suite)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white font-semibold rounded shadow-sm text-sm transition-all hover:bg-blue-700 cursor-pointer"
          >
            <ScrollTextIcon size={16} />
            View Previous Results
          </button>

          {activeRunId ? (
            <button
              onClick={handleStop}
              disabled={isStopping}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-red-600 font-semibold rounded hover:bg-red-50 transition-all text-sm cursor-pointer"
            >
              {isStopping ? <Loader2 className="animate-spin" size={16} /> : <Square size={16} />}
              Stop Test
            </button>
          ) : (
            <button
              onClick={handleStart}
              disabled={isRequestingStart || !isConnected || !hasTestCases}
              className={`
          flex items-center gap-2 px-4 py-2 font-semibold rounded shadow-sm text-sm transition-all cursor-pointer
          ${
            isRequestingStart || !isConnected || !hasTestCases
              ? "bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200"
              : "bg-orange-500 text-white hover:bg-orange-600 border border-orange-600"
          }
        `}
            >
              {isRequestingStart ? (
                <Loader2 className="animate-spin" size={16} />
              ) : (
                <Play size={16} />
              )}
              Start Load Test
            </button>
          )}
        </div>
      </div>

      <div className="bg-gray-50 border-b border-gray-200 px-8 py-6">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
          {summaryMetrics.map((metric, idx) => (
            <div key={idx} className="flex flex-col">
              <div className="flex items-center gap-1 text-gray-500 mb-1">
                <span className="text-xs font-medium uppercase tracking-wide">{metric.label}</span>
                {idx === 0 && <AlertCircle size={12} className="text-gray-400" />}
              </div>
              <div className={`text-2xl font-medium ${metric.color || "text-gray-900"}`}>
                {metric.value}
                {metric.unit && (
                  <span className="text-sm text-gray-500 ml-1 font-normal">{metric.unit}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto bg-white">
        <div className="px-8 py-6 space-y-8">
          <div className="w-full">
            <PerformanceMetricsGraph data={history} />
          </div>

          {/* TEST CONFIGURATION / DETAILS */}
          <div className="border-t border-gray-100 pt-6">
            <div className="flex items-center gap-2 mb-4">
              <Settings size={16} className="text-gray-400" />
              <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide">
                Test Parameters
              </h3>
            </div>

            <div className="bg-gray-50 rounded-lg border border-gray-200 p-5">
              <div
                className={`grid grid-cols-1 md:grid-cols-3 ${
                  PARAMS_GRID_COLS[config.loadProfileType] || PARAMS_GRID_COLS.FIXED
                } gap-6 transition-all`}
              >
                {/* Load Profile */}
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-2">
                    Load Profile
                  </label>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    variant="outlined"
                    disabled={!!activeRunId || isRequestingStart}
                    value={config.loadProfileType}
                    onChange={(e) => setConfig((p) => ({ ...p, loadProfileType: e.target.value }))}
                    sx={{
                      "& .MuiInputBase-root": { fontSize: "0.875rem", backgroundColor: "white" },
                      "& fieldset": { borderColor: "#e5e7eb" },
                    }}
                  >
                    <MenuItem value="FIXED">Fixed Load</MenuItem>
                    <MenuItem value="RAMP_UP">Ramp Up</MenuItem>
                    <MenuItem value="SPIKE">Spike</MenuItem>
                  </TextField>
                </div>

                {/* Virtual Users - FIXED only. RAMP_UP and SPIKE use Start/End Users
                    below instead, so showing this too would just be a second,
                    disconnected number the test ignores. */}
                {!usesStartEndUsers && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-2">
                      Virtual Users (VU)
                    </label>
                    <div className="relative">
                      <Users
                        size={14}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                      />
                      <input
                        type="number"
                        disabled={!!activeRunId || isRequestingStart}
                        className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all disabled:bg-gray-100"
                        value={config.virtualUsers}
                        onChange={(e) => setConfig({ ...config, virtualUsers: e.target.value })}
                        min="1"
                      />
                    </div>
                  </div>
                )}

                {usesStartEndUsers && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-2">
                      {isSpike ? "Start Users (Baseline)" : "Start Users"}
                    </label>
                    <div className="relative">
                      <ArrowUpRight
                        size={14}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                      />
                      <input
                        type="number"
                        disabled={!!activeRunId || isRequestingStart}
                        className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all disabled:bg-gray-100"
                        value={config.startUsers}
                        onChange={(e) => setConfig({ ...config, startUsers: e.target.value })}
                        min="0"
                      />
                    </div>
                  </div>
                )}

                {usesStartEndUsers && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-2">
                      {isSpike ? "End Users (Peak)" : "End Users"}
                    </label>
                    <div className="relative">
                      <ArrowDownRight
                        size={14}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                      />
                      <input
                        type="number"
                        disabled={!!activeRunId || isRequestingStart}
                        className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all disabled:bg-gray-100"
                        value={config.endUsers}
                        onChange={(e) => setConfig({ ...config, endUsers: e.target.value })}
                        min="0"
                      />
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-2">
                    Duration (mins)
                  </label>
                  <div className="relative">
                    <Clock
                      size={14}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                    />
                    <input
                      type="number"
                      disabled={!!activeRunId || isRequestingStart}
                      className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all disabled:bg-gray-100"
                      value={config.duration}
                      onChange={(e) => setConfig({ ...config, duration: e.target.value })}
                      min="1"
                    />
                  </div>
                </div>

                {/* Ramp-up time only means anything to the RAMP_UP profile. */}
                {isRampUp && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-2">
                      Ramp-up (sec)
                    </label>
                    <div className="relative">
                      <TrendingUp
                        size={14}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                      />
                      <input
                        type="number"
                        disabled={!!activeRunId || isRequestingStart}
                        className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all disabled:bg-gray-100"
                        value={config.rampUpTime}
                        onChange={(e) => setConfig({ ...config, rampUpTime: e.target.value })}
                        min="0"
                      />
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-2">
                    Think Time (sec)
                  </label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs font-bold">
                      s
                    </div>
                    <input
                      type="number"
                      disabled={!!activeRunId || isRequestingStart}
                      className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all disabled:bg-gray-100"
                      value={config.thinkTime}
                      onChange={(e) => setConfig({ ...config, thinkTime: e.target.value })}
                      min="0"
                      step="0.1"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PerformanceTesting;
